import { SOCKET_URL } from '@/lib/constants/env';
import { io } from 'socket.io-client';

export default {
  data() {
    return {
      appName: 'loan-pro',
      /**
       * Socket IO instance
       */
      socketio: io(SOCKET_URL),

      /**
       * This indicates if socket mixin should ignore the listener
       */
      useSocketEmitOnly: false
    };
  },

  created() {
    if (!this.useSocketEmitOnly) {
      this.socketio.on('connect', () => {
        console.info('SOCKET: CONNECTED');
      });

      this.socketio.on('disconnect', () => {
        console.info('SOCKET: DISCONNECTED');
      });

      //Listen for all loan-pro events
      this.socketio.on('events', (event) => {
        if (event.app != 'loan-pro') return;

        //check if mixin is being used in layout and refresh counters
        if (this.updateBadgeCount) {
          this.updateBadgeCount();
          return;
        }

        //refresh loanpro tab totals
        if (this.getLoanPoolProgressCount) {
          this.getLoanPoolProgressCount();
        }

        this.handleEventType(event.type, event.data);
      });
    }
  },

  methods: {
    /**
     * Broadcast an app event to all online users
     * @param {*} data
     */
    emitEvent({ type, message, data }) {
      this.socketio.emit('events', {
        app: this.appName,
        type,
        message,
        data
      });
    },

    /**
     * Initiates the correct actions of the event type that has occurred
     * @param { 'termination' | 'unassignment' | 'approval' } type The event type that occurred
     * @param {*} event
     */
    handleEventType(type, data) {
      //Check if user is on the loan pool page and update the loan if the type is not termination
      const isLoanPool = this.$route.name.indexOf('loan-pool') !== -1;
      if (isLoanPool && type != 'termination') {
        this.handleLoanUpdate(data);
        return;
      }

      switch (type) {
        case 'unassignment':
        case 'termination':
          this.handleLoanUnassignmentOrTermination(type, data, isLoanPool);
          break;
        case 'rejection':
          this.handleLoanRejection(data, isLoanPool);
          break;
        case 'assignment':
          this.handleLoanAssignment(data);
          break;
        case 'approval-assignment':
        case 'request-cancellation':
        case 'approval-request-rejected':
        case 'request-waiver':
        case 'request-waiver-rejected':
        case 'request-waiver-approved':
          console.log(this.searchLoanPool                              );

          if (this.searchLoanPool) this.searchLoanPool();
          break;
        default:
          this.handleLoanUpdate(data);
      }
    },

    /**
     * Gets called when a loan is assigned
     * @param {*} data
     */
    handleLoanAssignment(data) {
      if (!this.tableData) return;

      const loan = data.loan;

      const isAssignedToCurrentUser =
        data.assignment?.assigneeId == this.user.id;

      if (isAssignedToCurrentUser) {
        this.tableData.unshift(loan);
        this.loanPoolTotal += 1;
        return;
      }

      const index = this.tableData.findIndex((el) => el.id == loan.id);
      if (index == -1) return;

      const loanInTable = this.tableData[index];
      if (loanInTable && !isAssignedToCurrentUser) {
        this.tableData.splice(index, 1);
        this.loanPoolTotal -= 1;
      }

      if (this.loan && this.loan.id === loan.id) {
        //Check if user is on the loan flow page and navigate them away
        const currentRoute = this.$route.name;
        if (currentRoute.indexOf('loan-flow') != -1) {
          this.$router.replace({ name: 'loan-pool' });
        }
      }
    },

    /**
     * Gets called when a loan is terminated or unassigned
     * @param { 'termination' | 'unassignment' } type The event type that occurred
     * @param {*} data - the event data
     * @param { Boolean } isLoanPool - indicates if the user is currently on the loan pool page
     */
    handleLoanUnassignmentOrTermination(type, data, isLoanPool = false) {
      const loan = data.loan;
      if (this.tableData) {
        const index = this.tableData.findIndex((el) => el.id == loan.id);
        if (index > -1) {
          if (type === 'termination' || !isLoanPool) {
            this.tableData.splice(index, 1);
            this.loanPoolTotal -= 1;
          } else if (isLoanPool) {
            this.tableData.splice(index, 1, loan);
          }
        }
      }

      //Check if user is on the loan flow page and navigate them away
      if (this.loan && this.loan.id === loan.id) {
        //Close Loan Application Dialog
        if (this.$refs.loanApplication) {
          this.$refs.loanApplication.closeModal();
        }

        //Check if user is on the loan flow page and navigate them away
        const currentRoute = this.$route.name;
        if (currentRoute.indexOf('loan-flow') != -1) {
          this.$router.replace({ name: 'loan-pool' });
        }
      }
    },

    /**
     * Gets called when a loan is rejected
     * @param {*} data - the event data
     * @param { Boolean } isLoanPool - indicates if the user is currently on the loan pool page
     */
    handleLoanRejection(data, isLoanPool = false) {
      const currentRoute = this.$route.name;
      const loan = data.loan;
      const assigneeId = loan.assignment?.assigneeId;

      if (this.tableData) {
        const index = this.tableData.findIndex((el) => el.id == loan.id);
        if (index > -1) {
          if (!isLoanPool) {
            //check if the user who the loan is assigned to is on the assignment view page
            // and add loan to their workload
            if (
              currentRoute.indexOf('assignment-view') != 1 &&
              this.user.id == assigneeId
            ) {
              this.tableData.push(loan);
            } else {
              //remove loan from user who loan was assigned to
              this.tableData.splice(index, 1);
              this.loanPoolTotal -= 1;
            }
          } else {
            //update loan in the loan pool table
            this.tableData.splice(index, 1, loan);
          }
        }
      }

      //Check if user is on the approval, waiver or disbursement page and navigate them away
      if (this.loan && this.loan.id === loan.id) {
        //Close Loan Application Dialog
        if (this.$refs.loanApplication) {
          this.$refs.loanApplication.closeModal();
        }

        this.$router.replace({ name: 'loan-pool' });
      }
    },

    /**
     * Gets called when loan information is updated
     * @param {*} data
     */
    handleLoanUpdate(data) {
      if (this.tableData && data.loan) {
        const loan = data.loan;
        const index = this.tableData.findIndex((el) => el.id == loan.id);
        if (index == -1) return;

        this.tableData.splice(index, 1, loan);
      }
    },

    /**
     * Emit event to all users that the loan was unassigned
     *
     * @param { Object } loan - the loan object
     **/
    emitLoanUnassignmentEvent(loan) {
      const assigneeId = loan.assignment?.assigneeId;
      delete loan.assignment;

      this.emitEvent({
        type: 'unassignment',
        message: `${this.user.username} unassigned a ${loan.loanType} from you.`,
        data: {
          userId: this.user.id,
          assigneeId,
          username: this.user.username,
          loan: loan
        }
      });
    },

    /**
     * Emit event to all users that the loan was unassigned
     *
     * @param { Object } loan - the loan object
     **/
    emitLoanRejectionEvent(loan) {
      const assigneeId = loan.assignment?.assigneeId;
      delete loan.assignment;

      loan.assignment = {
        assignee: loan.processingOfficer,
        assigneeId: loan.processingOfficer.id
      };

      this.emitEvent({
        type: 'request-waiver',
        message: `${this.user.username} rejected a ${loan.loanType} from you.`,
        data: {
          userId: this.user.id,
          assigneeId,
          username: this.user.username,
          loan: loan
        }
      });
    },

    /**
     * Emit event to all users that loan was assign to yourself
     *
     * @param { Object } loan - the loan object
     **/
    emitAssignLoanToSelfEvent(loan) {
      loan.assignment = {
        assignee: this.user,
        assigneeId: this.user.id
      };

      this.emitEvent({
        type: 'update',
        message: ``,
        data: {
          userId: this.user.id,
          assigneeId: this.user.id,
          username: this.user.username,
          loan: loan
        }
      });
    },

    /**
     * Emit event to all users that loan was terminated
     *
     * @param { Object } loan - the loan object
     **/
    emitLoanTerminationEvent(loan) {
      this.emitEvent({
        type: 'termination',
        message: `${this.user.username} terminated a ${loan.loanType} loan assigned you.`,
        data: {
          userId: this.user.id,
          assigneeId: loan.assignment?.assignee?.id,
          username: this.user.username,
          loan: loan
        }
      });
    },

    /**
     * Emit event to all users that loan was deleted
     *
     * @param { Object } loan - the loan object
     **/
    emitLoanDeletionEvent(loan) {
      this.emitEvent({
        type: 'termination',
        message: `${this.user.username} permanently deleted a ${loan.loanType} loan assigned you.`,
        data: {
          userId: this.user.id,
          assigneeId: loan.assignment?.assignee?.id,
          username: this.user.username,
          loan: loan
        }
      });
    },

    /**
     * Emit event to all users that loan was assigned to a user
     *
     * @param { Object } loan - the loan object
     **/
    emitAssignLoanToUserEvent(loan, assignedUser) {
      loan.assignment = {
        assignee: assignedUser,
        assigneeId: assignedUser.id
      };

      this.emitEvent({
        type: 'assignment',
        message: `${this.user.username} assigned a ${loan.loanType} loan to you.`,
        data: {
          userId: this.user.id,
          assigneeId: assignedUser.id,
          username: this.user.username,
          loan: loan
        }
      });
    }
  }
};
